import { remark } from 'remark'
import remarkGfm from 'remark-gfm'
import { visit } from 'unist-util-visit'

/**
 * Simple AST-based table normalizer to fix common table formatting issues
 * Replaces regex hacks with proper markdown AST processing
 */
export function normalizeTablesSimple(content: string): string {
  if (!content || typeof content !== 'string') {
    return content
  }

  try {
    // Process markdown with remark to get AST
    const processor = remark().use(remarkGfm)
    const ast = processor.parse(content)

    let hasChanges = false

    // Visit all table nodes and fix structure issues
    visit(ast, 'table', (node: any) => {
      if (!node.children || node.children.length < 2) {
        return // Skip malformed tables
      }

      // Fix table structure at AST level
      const [header, separator, ...rows] = node.children

      // Ensure header row exists
      if (!header || header.type !== 'tableRow') {
        return
      }

      // Ensure separator row exists
      if (!separator || separator.type !== 'tableRow') {
        return
      }

      // Fix header cells
      if (header.children) {
        header.children = header.children.map((cell: any) => {
          if (cell.type === 'tableCell' && (!cell.children || cell.children.length === 0)) {
            // Add empty text node to empty cells
            cell.children = [{ type: 'text', value: '' }]
            hasChanges = true
          }
          return cell
        })
      }

      // Fix data rows
      rows.forEach((row: any) => {
        if (row.type === 'tableRow' && row.children) {
          row.children = row.children.map((cell: any) => {
            if (cell.type === 'tableCell' && (!cell.children || cell.children.length === 0)) {
              // Add empty text node to empty cells
              cell.children = [{ type: 'text', value: '' }]
              hasChanges = true
            }
            return cell
          })
        }
      })
    })

    // If we made changes, re-stringify the AST
    if (hasChanges) {
      return processor.stringify(ast)
    }

    return content

  } catch (error) {
    console.warn('Table normalization failed, using original content:', error)
    return content
  }
}

/**
 * Simple validation to check if content contains malformed tables
 */
export function hasMalformedTables(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false
  }

  try {
    const processor = remark().use(remarkGfm)
    const ast = processor.parse(content)

    let hasIssues = false

    visit(ast, 'table', (node: any) => {
      // Check for common table issues
      if (!node.children || node.children.length < 2) {
        hasIssues = true
        return
      }

      const [header, separator] = node.children

      if (!header || header.type !== 'tableRow' ||
          !separator || separator.type !== 'tableRow') {
        hasIssues = true
        return
      }
    })

    return hasIssues

  } catch (error) {
    // If parsing fails, assume there are malformed tables
    return true
  }
}